"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import Icon, {ICON_SIZES} from "@/components/library/Icon";

export default function AudioPreviewButton({
   url,
   label,
   volume,
   voiceUrl,
   changeMarker,
   setPlaying,
   size = ICON_SIZES.XXL,
}: Readonly<{
   url: string;
   changeMarker: string;
   setPlaying: (audioPlaying: string) => void;
   label?: string;
   volume?: number; // 0.0 – 1.0
   voiceUrl?: string; // agent voice sample to play simultaneously
   size?: ICON_SIZES;
}>) {
   const [isPlaying, setIsPlaying] = useState<boolean>(false);
   const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
   const audioRef = useRef<HTMLAudioElement | null>(null);
   const voiceAudioRef = useRef<HTMLAudioElement | null>(null);

   const stopPlayback = useCallback(() => {
      const current = audioRef.current;
      if (current) {
         current.pause();
         current.currentTime = 0;
      }
      if (voiceAudioRef.current) {
         voiceAudioRef.current.pause();
         voiceAudioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
   }, []);

   const handleEnded = useCallback(() => {
      setIsPlaying(false);
   }, []);

   useEffect(() => {
      audio?.addEventListener("ended", handleEnded);

      return () => {
         audio?.removeEventListener("ended", handleEnded);
      };
   }, [audio, handleEnded]);

   // Stop voice audio when main audio stops
   useEffect(() => {
      if (!isPlaying && voiceAudioRef.current) {
         voiceAudioRef.current.pause();
         voiceAudioRef.current.currentTime = 0;
      }
   }, [isPlaying]);

   useEffect(() => {
      if (isPlaying && changeMarker !== url) {
         stopPlayback();
      }
   }, [changeMarker, isPlaying, stopPlayback, url]);

   // Sync volume changes to the playing audio element in real-time
   useEffect(() => {
      if (audio && volume !== undefined) {
         audio.volume = volume;
      }
   }, [audio, volume]);

   // Reset audio element when url changes so the new file plays
   useEffect(() => {
      const current = audioRef.current;
      if (current) {
         current.pause();
         current.currentTime = 0;
         audioRef.current = null;
         setAudio(null);
         setIsPlaying(false);
      }

      return () => {
         if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
         }
      };
   }, [url]);

   // Reset voice audio when voiceUrl changes
   useEffect(() => {
      if (voiceAudioRef.current) {
         voiceAudioRef.current.pause();
         voiceAudioRef.current = null;
      }
   }, [voiceUrl]);

   function handlePlay() {
      if (!url) return;

      const current = audioRef.current ?? audio;
      const isCurrentlyPlaying = isPlaying || (current != null && current.paused === false);

      if (isCurrentlyPlaying) {
         stopPlayback();
         return;
      }

      if (volume === 0) return;

      const audioToUse = current ?? new Audio(url);
      audioRef.current = audioToUse;
      if (audioToUse !== audio) {
         setAudio(audioToUse);
      }

      if (volume !== undefined) audioToUse.volume = volume;
      setPlaying(url);
      setIsPlaying(true);
      audioToUse.play().catch(() => {
         setIsPlaying(false);
      });

      if (voiceUrl) {
         const voice = voiceAudioRef.current ?? new Audio(voiceUrl);
         voiceAudioRef.current = voice;
         voice.volume = 0.7;
         voice.currentTime = 0;
         voice.play().catch(() => {});
      }
   }

   return (
      <button
         type="button"
         onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePlay();
         }}
         className="group inline-flex items-center gap-1 !p-[6px] border-none rounded-lg"
      >
         {isPlaying ? (
            <Icon icon="circle-pause" size={size} className="text-blue-600 dark:text-blue-400" />
         ) : (
            <Icon icon="circle-play" size={size} className="text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors" />
         )}
         {label && <span className="text-sm">{label}</span>}
      </button>
   );
}
